import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { billingApi, dispensaries as dApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { Sparkles, Check, ArrowRight, Zap, Crown, Megaphone, Loader2 } from "lucide-react";

const ICONS = {
  pro_monthly: Crown,
  featured: Megaphone,
  flash_boost: Zap,
  wallet_topup_100: Sparkles,
  wallet_topup_250: Sparkles,
  wallet_topup_500: Sparkles,
};
const PERKS = {
  pro_monthly: ["Unlimited deals & products", "Full ROI analytics", "Bud catalog inclusion", "Priority support"],
  featured: ["Pinned on map + city", "Priority in AI picks", "2 sponsored flash deals included"],
  flash_boost: ["Top of /deals page · 48h", "Push to opted-in users in radius", "Avg 3.4× SKU lift"],
  wallet_topup_100: ["Funds 8–10 verified visits", "Auto-deduct on QR redemption"],
  wallet_topup_250: ["Funds 20–25 verified visits", "Auto-deduct on QR redemption"],
  wallet_topup_500: ["Funds 40–50 verified visits", "Auto-deduct on QR redemption"],
};

export default function Billing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [packages, setPackages] = useState([]);
  const [dispensary, setDispensary] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      const r = await billingApi.packages();
      setPackages(r.packages || []);
      if (user) {
        const all = await dApi.list().catch(() => []);
        const d = all.find((x) => x.owner_user_id === user.id) || (user.dispensary_id ? await dApi.get(user.dispensary_id).catch(() => null) : null);
        if (d) setDispensary(d);
      }
    })();
  }, [user]);

  const checkout = async (pid) => {
    if (!user) { nav("/auth?mode=login"); return; }
    setBusy(pid);
    try {
      const r = await billingApi.checkout(pid, dispensary?.id);
      if (r?.url) {
        window.location.href = r.url;
      } else {
        toast.error("Couldn't start checkout");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setBusy("");
    }
  };

  const subTier = packages.filter((p) => p.kind === "subscription");
  const addon = packages.filter((p) => p.kind === "addon");
  const wallet = packages.filter((p) => p.kind === "wallet");

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 480, height: 480, background: "#15FF73", top: -120, right: -100 }} />
      <div className="blob" style={{ width: 380, height: 380, background: "#FF1A8C", bottom: -180, left: -120, animationDelay: "-6s" }} />
      <NavBar />
      <Toaster position="top-right" theme="light" richColors />
      <div className="max-w-5xl mx-auto px-5 lg:px-10 py-14 relative">
        <span className="sticker sticker-magenta mb-3 inline-block">Billing · pay-as-you-grow</span>
        <h1 className="font-display text-6xl sm:text-7xl font-extrabold leading-[0.88] tracking-[-0.04em] mb-5 mt-3">
          Less is <span className="text-rainbow">more.</span>
        </h1>
        <p className="text-lg text-[#0B0F0C]/70 max-w-2xl mb-12 leading-relaxed">
          One small monthly fee. Top up your wallet whenever. <span className="font-script text-2xl text-[#FF1A8C]">— that's the whole pitch.</span>
        </p>

        {!dispensary && user && (
          <div className="card-x card-magenta bg-[#FFE74C] p-5 mb-8 text-sm font-medium">
            Sign in as a shop owner first — we'll link wallet credits to your dispensary on success.
          </div>
        )}

        {/* PRO TIER */}
        <Section title="Subscription" subtitle="The whole platform, monthly.">
          <div className="grid md:grid-cols-2 gap-5">
            {subTier.map((p) => (
              <PriceCard key={p.id} p={p} onBuy={() => checkout(p.id)} busy={busy === p.id} highlight tone="card-magenta" perks={PERKS[p.id]} />
            ))}
            <FreeCard />
          </div>
        </Section>

        {/* ADD-ONS */}
        <Section title="Boosts" subtitle="Optional. Use when you want lift.">
          <div className="grid md:grid-cols-2 gap-5">
            {addon.map((p) => (
              <PriceCard key={p.id} p={p} onBuy={() => checkout(p.id)} busy={busy === p.id} tone="card-cyan" perks={PERKS[p.id]} />
            ))}
          </div>
        </Section>

        {/* WALLET TOP-UPS */}
        <Section title="Wallet top-ups" subtitle="Prepaid. Funds your $3–5 per-visit commission. Auto-pause at $0.">
          <div className="grid md:grid-cols-3 gap-5">
            {wallet.map((p) => (
              <PriceCard key={p.id} p={p} onBuy={() => checkout(p.id)} busy={busy === p.id} tone="card-green" perks={PERKS[p.id]} compact />
            ))}
          </div>
        </Section>

        <div className="mt-14 text-xs font-mono-x font-bold uppercase tracking-widest opacity-55 text-center">
          Powered by Stripe · Test mode · No real charges
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="mb-12">
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-1 tracking-tight">{title}</h2>
      <p className="text-sm text-[#0B0F0C]/55 mb-5">{subtitle}</p>
      {children}
    </div>
  );
}

function FreeCard() {
  return (
    <div className="card-x card-cyan bg-[#FAF6E8] p-7 flex flex-col">
      <div className="label-overline text-[#FF1A8C] mb-2">Free</div>
      <h3 className="font-display text-3xl font-extrabold leading-tight mb-1">Starter</h3>
      <div className="font-mono-x font-bold text-2xl mt-2 mb-4">$0</div>
      <ul className="space-y-2 text-sm flex-1">
        {["List 1 shop", "Up to 3 products", "1 active deal", "GAS / PASS reviews"].map((d) => (
          <li key={d} className="flex gap-2"><Check size={14} strokeWidth={2.4} className="text-[#15FF73] mt-0.5 flex-shrink-0" /><span>{d}</span></li>
        ))}
      </ul>
      <div className="mt-5 text-xs font-mono-x font-bold uppercase tracking-widest opacity-60">Always included</div>
    </div>
  );
}

function PriceCard({ p, onBuy, busy, highlight, tone, perks, compact }) {
  const Icon = ICONS[p.id] || Sparkles;
  return (
    <div data-testid={`pkg-${p.id}`} className={`card-x ${tone} bg-[#FAF6E8] p-7 flex flex-col`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} strokeWidth={2.4} />
        <span className="label-overline text-[#FF1A8C]">{highlight ? "Most popular" : (p.kind === "wallet" ? "Top-up" : "Boost")}</span>
      </div>
      <h3 className={`font-display ${compact ? "text-2xl" : "text-3xl"} font-extrabold leading-tight`}>{p.label}</h3>
      <div className="font-mono-x font-bold text-2xl mt-2 mb-4">
        ${p.amount.toFixed(0)}<span className="text-sm opacity-55">{p.kind === "subscription" ? " / month" : ""}</span>
      </div>
      <ul className="space-y-2 text-sm flex-1 mb-5">
        {(perks || []).map((d) => (
          <li key={d} className="flex gap-2"><Check size={14} strokeWidth={2.4} className="text-[#15FF73] mt-0.5 flex-shrink-0" /><span>{d}</span></li>
        ))}
      </ul>
      <button
        data-testid={`buy-${p.id}`}
        onClick={onBuy}
        disabled={!!busy}
        className={`btn-x ${highlight ? "btn-primary" : "btn-ghost"} disabled:opacity-50 w-full justify-center`}
      >
        {busy ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : <>Buy <ArrowRight size={14} strokeWidth={2.4} /></>}
      </button>
    </div>
  );
}
